import {
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Chip,
  Spinner,
  addToast,
} from "@heroui/react";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeSelectInput } from "@/Components/NextBase/LimeSelectInput";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import * as Sentry from "@sentry/react";
import { api } from "@/lib/api-client";
import { useClientFeature } from "@/hooks/useClientFeature";
import { useState } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import { Search, X } from "lucide-react";
import type { GetOrganizationInvoices } from "@/server-types";
import { cn } from "@/utils";

type Props = {
  invoiceId: number | null;
  customerId: number | null;
  /** Whose share the document settles. Null is the subscription holder's. */
  payerCompanyId?: number | null;
  payerName?: string | null;
  onClose: () => void;
};

type DocumentRow = GetOrganizationInvoices["response"]["invoices"][number];

export function SubscriptionInvoiceLinkDocumentModal({
  invoiceId,
  customerId,
  payerCompanyId = null,
  payerName = null,
  onClose,
}: Props) {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 200);
  const [selectedDocument, setSelectedDocument] = useState<DocumentRow | null>(
    null,
  );

  const { isFeatureEnabled } = useClientFeature();

  const { data: posAuthData, isFetching: isFetchingPosAuthData } =
    api.cookie.useGetPosToken();

  const trimmedSearchQuery = debouncedSearchQuery.trim();
  const hasMinSearchLength = trimmedSearchQuery.length >= 3;

  const documentsDisabled =
    invoiceId == null ||
    (payerCompanyId == null && customerId == null) ||
    !posAuthData ||
    isFetchingPosAuthData ||
    !hasMinSearchLength;

  const { data: invoicesData, isFetching: isFetchingInvoices } =
    api.taxRegister.useGetOrganizationInvoices({
      page: 1,
      perPage: 10,
      type: "invoice",
      query: trimmedSearchQuery,
      disabled: documentsDisabled,
    });

  const { data: estimatesData, isFetching: isFetchingEstimates } =
    api.taxRegister.useGetOrganizationInvoices({
      page: 1,
      perPage: 10,
      type: "estimate",
      query: trimmedSearchQuery,
      disabled: documentsDisabled || !isFeatureEnabled("taxRegisterEstimates"),
    });

  const {
    mutateAsync: linkDocument,
    isPending,
    processedErrorMessage,
    reset: resetLinkDocument,
  } = api.customer.useLinkSubscriptionPeriodDocument();

  const isSearching = isFetchingInvoices || isFetchingEstimates;

  const documents = [
    // The invoice listing also returns credit notes, which cannot be linked.
    ...(invoicesData?.invoices ?? []).filter(
      (d) => !d.canceled && d.type === "invoice",
    ),
    // Estimates already converted to an invoice are skipped — the issued
    // invoice is the document to link in that case.
    ...(estimatesData?.invoices ?? []).filter(
      (d) => !d.canceled && !d.hasIssuedInvoiceFromEstimate,
    ),
  ];

  // The tax register stamps the party the document was issued to, so check the payer, not always the customer.
  function belongsToOtherPayer(document: DocumentRow): boolean {
    if (payerCompanyId != null) {
      return (
        document.documentCompanyId != null &&
        Number(document.documentCompanyId) !== payerCompanyId
      );
    }
    return (
      document.documentCustomerId != null &&
      Number(document.documentCustomerId) !== customerId
    );
  }

  const items = documents.map((document) => ({
    key: document.id,
    label: (
      <DocumentItemLabel
        document={document}
        belongsToOtherPayer={belongsToOtherPayer(document)}
      />
    ),
    document,
  }));

  const handleClose = () => {
    setSearchQuery("");
    setSelectedDocument(null);
    resetLinkDocument();
    onClose();
  };

  const handleSelect = (id: string) => {
    const match = documents.find((d) => d.id === id);
    if (!match) return;
    if (belongsToOtherPayer(match)) return;
    setSelectedDocument(match);
    setSearchQuery("");
  };

  const handleClearSelection = () => {
    setSelectedDocument(null);
    resetLinkDocument();
  };

  const handleConfirm = async () => {
    if (
      invoiceId == null ||
      selectedDocument == null ||
      !posAuthData?.organizationId
    ) {
      return;
    }
    try {
      await linkDocument({
        invoiceId,
        body: {
          spaceInvoicesDocumentId: selectedDocument.id,
          organizationId: posAuthData.organizationId,
          payerCompanyId,
        },
      });
      addToast({
        title: t`Dokument je bil povezan z obdobjem`,
        color: "success",
      });
      handleClose();
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return (
    <LimeModal
      isOpen={invoiceId != null}
      onClose={handleClose}
      data-identifier="subscription-invoice-link-document-modal"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>
              <Trans>Poveži obstoječi dokument</Trans>
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-3">
                <p className="text-default-700 text-sm">
                  {payerCompanyId != null && payerName ? (
                    <Trans>
                      Poiščite obstoječi dokument iz davčne blagajne, ki pokriva
                      delež podjetja {payerName} v tem obdobju.
                    </Trans>
                  ) : (
                    <Trans>
                      Poiščite obstoječi dokument iz davčne blagajne, ki bo
                      povezan s tem obdobjem paketa.
                    </Trans>
                  )}
                </p>

                {selectedDocument == null ? (
                  <LimeSelectInput
                    inputType="text"
                    inputProps={{
                      placeholder: t`Išči po številki dokumenta (vsaj 3 znaki)`,
                      value: searchQuery,
                      onChange: (e) => setSearchQuery(e.target.value),
                      startContent: <Search size={16} />,
                      endContent: isSearching ? <Spinner size="sm" /> : null,
                      autoComplete: "do-not-autofill",
                      "data-identifier": "link-document-search",
                    }}
                    items={items}
                    onSelect={handleSelect}
                    selectFirstOnEnter
                  />
                ) : (
                  <div
                    className="border-default-200 flex items-center justify-between gap-3 rounded-xl border p-3"
                    data-identifier="link-document-selected"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Chip size="sm" variant="flat">
                        {selectedDocument.type === "invoice"
                          ? t`Račun`
                          : t`Predračun`}
                      </Chip>
                      <span className="text-sm font-medium">
                        {selectedDocument.number}
                      </span>
                      <span className="text-default-500 text-xs">
                        {selectedDocument.totalFormatted} ·{" "}
                        {selectedDocument.dateFormatted}
                      </span>
                    </div>
                    <Button
                      variant="bordered"
                      size="sm"
                      startContent={<X />}
                      onPress={handleClearSelection}
                      data-identifier="clear-link-document-button"
                    >
                      <Trans>Odstrani</Trans>
                    </Button>
                  </div>
                )}

                <LimeAlert color="danger" message={processedErrorMessage} />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={handleClose}>
                <Trans>Prekliči</Trans>
              </Button>
              <Button
                color="primary"
                onPress={handleConfirm}
                isDisabled={selectedDocument == null}
                isLoading={isPending}
                data-identifier="confirm-link-document-button"
              >
                <Trans>Poveži</Trans>
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </LimeModal>
  );
}

function DocumentItemLabel({
  document,
  belongsToOtherPayer,
}: {
  document: DocumentRow;
  belongsToOtherPayer: boolean;
}) {
  const previewItemCount = 3;
  const itemNames = document.itemNames ?? [];
  const previewNames = itemNames.slice(0, previewItemCount);
  const remainingCount = itemNames.length - previewNames.length;

  return (
    <div className="flex flex-1 flex-col gap-1">
      <div
        className={cn(
          "flex flex-col gap-1",
          belongsToOtherPayer && "opacity-50",
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col">
            <span className="text-sm font-medium">{document.number}</span>
            <span className="text-default-500 text-xs">
              {document.dateFormatted}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">{document.totalFormatted}</span>
            <Chip size="sm" variant="flat">
              {document.type === "invoice" ? t`Račun` : t`Predračun`}
            </Chip>
          </div>
        </div>
        {previewNames.length > 0 && (
          <p className="text-default-500 line-clamp-2 text-xs">
            {previewNames.join(", ")}
            {remainingCount > 0 ? ` +${remainingCount}` : ""}
          </p>
        )}
      </div>
      {belongsToOtherPayer && (
        <p className="text-danger text-xs">
          <Trans>
            Ta dokument je izdan drugemu plačniku in ga ni mogoče povezati.
          </Trans>
        </p>
      )}
    </div>
  );
}
